import { useState, useEffect } from 'react';

/**
 * Drop-in replacement for `useState` that persists the value to localStorage
 * under `key`, so it survives component remounts (e.g. switching tabs) and
 * page reloads.
 *
 * - On mount, lazily reads `localStorage.getItem(key)` and JSON.parses it.
 *   If the key is missing, or parsing fails for any reason (malformed JSON
 *   left over from a previous version of the filter's shape, localStorage
 *   being unavailable in private browsing, etc.), it silently falls back to
 *   `defaultValue` instead of throwing.
 * - Whenever the value changes, it's written back to localStorage. Write
 *   failures (quota exceeded, private-mode restrictions, etc.) are caught
 *   and ignored so they never crash the page.
 * - No TTL/expiry: once set, a value stays persisted until the caller sets
 *   it again.
 *
 * @param {string} key - unique localStorage key for this filter field.
 * @param {*} defaultValue - value used when nothing valid is stored yet.
 * @returns {[*, Function]} same shape as `useState`: [value, setValue].
 */
export function usePersistedFilterState(key, defaultValue) {
    const [value, setValue] = useState(() => {
        try {
            const saved = localStorage.getItem(key);
            if (saved === null || saved === undefined) {
                return defaultValue;
            }
            return JSON.parse(saved);
        } catch (e) {
            return defaultValue;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            // Ignore persistence failures (quota exceeded, private mode, etc.)
            // — losing the ability to persist a filter shouldn't crash the page.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, value]);

    return [value, setValue];
}
